<template>
  <v-row ref="self" class="overview-item">
    <v-col cols="12" class="d-flex ga-3">
      <div class="overview-item__title text-h6 text-sm-h5 font-weight-bold">
        {{ title }}
      </div>
      <v-btn
        v-if="collapsable && items.length > 0"
        class="no-print"
        icon
        size="x-small"
        @click="collapse"
      >
        <v-icon class="d-flex">
          {{ collapsed ? 'fa fa-eye-slash' : 'fa fa-eye' }}
        </v-icon>
      </v-btn>
    </v-col>
    <v-col>
      <v-data-table
        class="bold-headers"
        :headers="headers"
        :items="items"
        hover
        hide-default-footer
        color="primary"
        :expand-on-click="expandOnClick"
        :items-per-page="-1"
        :group-by="groupBy"
      >
        <template v-if="collapsed" #body />
        <template #body.append="{ headers: slotHeaders }">
          <template v-if="items.length > 0">
            <tr v-for="footer, footerIndex in footers" :key="footerIndex">
              <td v-for="header in slotHeaders[0]" :key="header.key" class="font-weight-bold">
                <slot :name="`footer.${header.key}`" :value="footer[header.key ?? '']" :header="header">
                  {{ footer[header.key ?? ''] }}
                </slot>
              </td>
            </tr>
          </template>
        </template>
        <template v-for="slot in Object.keys(slots)" #[slot]="data">
          <template v-if="items.length > 0 || !slot.includes('item.')">
            <slot :name="slot" v-bind="data" />
          </template>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, useSlots, useTemplateRef } from 'vue'
import { usePrintingStore } from '@/stores/printing'
import type { VRow } from 'vuetify/lib/components'
import { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort'

export interface Header {
  key: string
  value: string
  title: string
  sortable: boolean
  printable?: boolean
}

export type Footer = Partial<Record<Header['key'], string | number>>

interface Props {
  title: string
  headers: Header[]
  items: unknown[]
  footers?: Footer[]
  expandOnClick?: boolean
  collapsable?: boolean
  groupBy?: SortItem[]
}

const self = useTemplateRef<VRow>('self')

const slots = useSlots()

const printing = usePrintingStore()

const emit = defineEmits<{
  collapse: [ boolean ]
}>()

const props = defineProps<Props>()

const headers = computed(() => {
  if (props.items.length === 0) return []
  return props.headers.filter(h => !hiddenOnPrint.value.includes(h.key))
})

// --- Printing ---
const hiddenOnPrint = ref<string[]>([])

// --- Collapsing ---
const collapsed = ref(false)

function collapse() {
  collapsed.value = !collapsed.value
  emit('collapse', collapsed.value)
}

onMounted(() => {
  printing.registerHooks({
    title: props.title,
    disabled: true,
    toggeable: true,
    children: props.headers.map(header => ({
      id: header.key,
      title: header.title,
      disabled: true,
      toggeable: true,
      beforePrint() {
        hiddenOnPrint.value.push(header.key)
      },
      afterPrint() {
        hiddenOnPrint.value = hiddenOnPrint.value.filter(h => h !== header.key)
      },
    })),
    beforePrint: () => self.value?.$el.classList.add('no-print'),
    afterPrint: () => self.value?.$el.classList.remove('no-print'),
  })
})
</script>
